/**
 * IDF Version Transition Types
 *
 * Types for defining and executing version transitions between EnergyPlus versions.
 */

import type { IDFDocument, IDFObject } from '../parser/types';

/**
 * Represents a version number in EnergyPlus format (e.g., "25.2", "26.1")
 */
export interface VersionNumber {
  major: number;
  minor: number;
}

/**
 * Result of a transition operation
 */
export interface TransitionResult {
  /** The transitioned document */
  document: IDFDocument;
  /** Whether the transition was successful */
  success: boolean;
  /** Any warnings generated during transition */
  warnings: TransitionWarning[];
  /** Any errors that occurred */
  errors: TransitionError[];
  /** Summary of changes made */
  changes: TransitionChange[];
}

/**
 * A warning generated during transition
 */
export interface TransitionWarning {
  /** Warning message */
  message: string;
  /** Object class name if applicable */
  objectClass?: string;
  /** Object name (first field) if applicable */
  objectName?: string;
  /** Field index if applicable */
  fieldIndex?: number;
}

/**
 * An error that occurred during transition
 */
export interface TransitionError {
  /** Error message */
  message: string;
  /** Object class name if applicable */
  objectClass?: string;
  /** Object name (first field) if applicable */
  objectName?: string;
}

/**
 * A change made during transition
 */
export interface TransitionChange {
  /** Type of change */
  type: 'version_update' | 'field_deleted' | 'field_added' | 'field_renamed' | 'object_renamed' | 'object_deleted' | 'variable_renamed';
  /** Object class name */
  objectClass: string;
  /** Object name (first field) if applicable */
  objectName?: string;
  /** Description of the change */
  description: string;
}

/**
 * Types of field transformations
 */
export type FieldTransformationType =
  | 'copy'           // Copy field as-is
  | 'delete'         // Remove field
  | 'rename'         // Field exists but name changed (copy value)
  | 'add_default'    // Add a new field with default value
  | 'transform';     // Apply custom transformation

/**
 * A single field mapping in an object transition rule
 */
export interface FieldMapping {
  /** Source field index (0-based) */
  sourceIndex: number;
  /** Target field index (0-based) */
  targetIndex: number;
  /** Type of transformation */
  type: FieldTransformationType;
  /** Default value for 'add_default' type */
  defaultValue?: string;
  /** Custom transformation function */
  transform?: (value: string, object: IDFObject) => string;
}

/**
 * Result of a multi-object transformation
 */
export interface MultiObjectResult {
  /** The primary transformed object (may be null if object is deleted/replaced) */
  primary: IDFObject | null;
  /** Additional objects created during transformation */
  additional: IDFObject[];
  /** Changes made during this transformation */
  changes: TransitionChange[];
  /** Warnings generated */
  warnings: TransitionWarning[];
}

/**
 * Object transition rule defining how to transform an object between versions
 */
export interface ObjectTransitionRule {
  /** Source object class name (case-insensitive) */
  sourceClass: string;
  /** Target object class name (may be same or different for renames) */
  targetClass: string;
  /** Field mappings from source to target (for simple transformations) */
  fieldMappings: FieldMapping[];
  /** Whether to delete this object type entirely */
  deleteObject?: boolean;
  /** Custom preprocessing function */
  preprocess?: (object: IDFObject) => IDFObject;
  /** Custom postprocessing function */
  postprocess?: (object: IDFObject) => IDFObject;
  /**
   * Custom multi-object transformation function.
   * Use this for complex transformations that create multiple output objects.
   * When provided, fieldMappings are ignored.
   */
  multiObjectTransform?: (object: IDFObject, document: IDFDocument) => MultiObjectResult;
}

/**
 * Report variable name mapping
 */
export interface VariableMapping {
  /** Original variable name (case-insensitive) */
  oldName: string;
  /** New variable name, or '<DELETE>' to remove */
  newName: string;
}

/**
 * A complete version transition definition
 */
export interface VersionTransition {
  /** Source version */
  fromVersion: VersionNumber;
  /** Target version */
  toVersion: VersionNumber;
  /** Object transition rules */
  objectRules: Map<string, ObjectTransitionRule>;
  /** Report variable name mappings */
  variableMappings: Map<string, string>;
  /** Objects that are deleted in the new version */
  deletedObjects: Set<string>;
  /** Objects renamed in the new version (old -> new) */
  renamedObjects: Map<string, string>;
}

/**
 * Parse a version string (e.g., "25.2" or "26.1.0") into components
 */
export function parseVersion(versionStr: string): VersionNumber {
  const parts = versionStr.split('.');
  return {
    major: parseInt(parts[0] || '0', 10),
    minor: parseInt(parts[1] || '0', 10),
  };
}

/**
 * Format a version number back to string
 */
export function formatVersion(version: VersionNumber): string {
  return `${String(version.major)}.${String(version.minor)}`;
}

/**
 * Compare two version numbers
 * Returns: negative if a < b, 0 if equal, positive if a > b
 */
export function compareVersions(a: VersionNumber, b: VersionNumber): number {
  if (a.major !== b.major) {
    return a.major - b.major;
  }
  return a.minor - b.minor;
}

/**
 * Create a simple copy mapping for a range of fields
 */
export function createCopyMappings(startIndex: number, count: number): FieldMapping[] {
  const mappings: FieldMapping[] = [];
  for (let i = 0; i < count; i++) {
    mappings.push({
      sourceIndex: startIndex + i,
      targetIndex: startIndex + i,
      type: 'copy',
    });
  }
  return mappings;
}

/**
 * Create a shifted copy mapping (for when fields are deleted before this range)
 */
export function createShiftedCopyMappings(
  sourceStart: number,
  targetStart: number,
  count: number
): FieldMapping[] {
  const mappings: FieldMapping[] = [];
  for (let i = 0; i < count; i++) {
    mappings.push({
      sourceIndex: sourceStart + i,
      targetIndex: targetStart + i,
      type: 'copy',
    });
  }
  return mappings;
}
